import { formatJsonForTextarea } from "@src/presentation/pages/Purchasers/utils/purchaserConfigTemplateFormUtils";
import type { PurchaserConfigTemplate } from "@src/domain/entities/purchaser";
import { DISCOUNT_PERCENT_MAX } from "./purchaserKeyValidation";

export { TERM_DAYS_MAX as TERM_DAYS_FIELD_MAX } from "./purchaserKeyValidation";

export type PurchaserAddEditFormValues = {
  catalogPurchaserId: number | null;
  purchaserName: string;
  purchaserKey: string;
  isActive: boolean;
  searchKeywords: string;
  defaultProductCategoryId: number | null;
  metadata: string;
  intelligenceFilter: string;
  discountPercentageCredit: string;
  deductionTypeInvoicePatternForDiscount: string;
  discountPercentageDeduction: string;
  discountTermAgreementDays: string;
  additionalFieldMapping: string;
  discountTermAgreementDaysTolerance: string;
};

export const EMPTY_PURCHASER_FORM_VALUES: PurchaserAddEditFormValues = {
  catalogPurchaserId: null,
  purchaserName: "",
  purchaserKey: "",
  isActive: true,
  searchKeywords: "",
  defaultProductCategoryId: null,
  metadata: "",
  intelligenceFilter: "",
  discountPercentageCredit: "",
  deductionTypeInvoicePatternForDiscount: "",
  discountPercentageDeduction: "",
  discountTermAgreementDays: "",
  additionalFieldMapping: "",
  discountTermAgreementDaysTolerance: "",
};

export function numberToFormValue(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}

export function templateToFormDefaults(
  template: PurchaserConfigTemplate | null | undefined,
): Pick<
  PurchaserAddEditFormValues,
  | "searchKeywords"
  | "metadata"
  | "intelligenceFilter"
  | "deductionTypeInvoicePatternForDiscount"
  | "additionalFieldMapping"
  | "discountTermAgreementDaysTolerance"
> {
  if (!template) {
    return {
      searchKeywords: "",
      metadata: "",
      intelligenceFilter: "",
      deductionTypeInvoicePatternForDiscount: "",
      additionalFieldMapping: "",
      discountTermAgreementDaysTolerance: "",
    };
  }

  return {
    searchKeywords: template.searchKeywords ?? "",
    metadata: formatJsonForTextarea(template.metadata),
    intelligenceFilter: formatJsonForTextarea(template.intelligenceFilter),
    deductionTypeInvoicePatternForDiscount: formatJsonForTextarea(
      template.deductionTypeInvoicePatternForDiscount,
    ),
    additionalFieldMapping: formatJsonForTextarea(
      template.additionalFieldMapping,
    ),
    discountTermAgreementDaysTolerance: numberToFormValue(
      template.discountTermAgreementDaysTolerance,
    ),
  };
}

export function parseOptionalPercentField(
  raw: string,
  fieldLabel: string,
): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    throw new TypeError(
      `${fieldLabel} must be a number between 0 and ${DISCOUNT_PERCENT_MAX} with at most 2 decimal places.`,
    );
  }
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > DISCOUNT_PERCENT_MAX) {
    throw new TypeError(
      `${fieldLabel} must be between 0 and ${DISCOUNT_PERCENT_MAX}.`,
    );
  }
  return value;
}

export function parseOptionalIntegerField(
  raw: string,
  fieldLabel: string,
  max?: number,
): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) {
    throw new TypeError("Please enter a valid number.");
  }
  const value = Number(trimmed);
  if (max != null && value > max) {
    throw new TypeError(`${fieldLabel} must be between 0 and ${max}.`);
  }
  if (max == null && !Number.isInteger(value)) {
    throw new TypeError("Please enter a valid number.");
  }
  return value;
}
